import { PersistedWorkflow } from '@cotera/sdk/core';
import type { AgentInputField } from './components/node/edit-strategies/agent-input-shape';
import { parseAgentInputShape } from './components/node/edit-strategies/agent-input-shape';
import type { WorkflowGraphApiData } from './workflow-graph.types';

const { WorkflowGraph } = PersistedWorkflow;

/** Entry nodes = definition nodes with no incoming edges (matches server run seeding). */
export function findEntryNodeIdsFromEdges(
  nodeIds: readonly string[],
  edges: Array<{ fromNodeId?: string; toNodeId: string }>
): string[] {
  return WorkflowGraph.create({
    nodes: nodeIds.map((id) => ({ id, inputShape: null })),
    edges: edges.map((edge) => ({
      fromNodeId: edge.fromNodeId ?? '',
      toNodeId: edge.toNodeId,
    })),
  }).entryNodeIds();
}

export function findWorkflowEntryNodeIds(
  graph: WorkflowGraphApiData
): string[] {
  return WorkflowGraph.create({
    nodes: graph.nodes.map((node) => ({
      id: node.id,
      inputShape: node.inputShape ?? null,
    })),
    edges: graph.edges.map((edge) => ({
      fromNodeId: edge.fromNodeId,
      toNodeId: edge.toNodeId,
    })),
  }).entryNodeIds();
}

export type WorkflowEntryRunInputSpec =
  | { kind: 'struct'; fields: AgentInputField[]; inputShape: string }
  | { kind: 'scalar'; type: string; inputShape: string }
  | { kind: 'empty' };

export type WorkflowEntryRunInputResolution =
  | { ok: true; spec: WorkflowEntryRunInputSpec }
  | { ok: false; reason: 'conflict' };

function mergeStructFields(
  shapes: Array<{ fields: AgentInputField[] }>
): AgentInputField[] | null {
  const merged = new Map<string, AgentInputField>();
  for (const shape of shapes) {
    for (const field of shape.fields) {
      const existing = merged.get(field.name);
      if (existing === undefined) {
        merged.set(field.name, field);
        continue;
      }
      if (
        existing.type !== field.type ||
        existing.required !== field.required
      ) {
        return null;
      }
    }
  }
  return [...merged.values()];
}

function normalizeInputShape(shape: string): string {
  return shape.trim();
}

export function resolveWorkflowEntryRunInputSpec(
  graph: WorkflowGraphApiData
): WorkflowEntryRunInputResolution {
  const entryNodeIds = new Set(findWorkflowEntryNodeIds(graph));
  if (entryNodeIds.size === 0) {
    return { ok: true, spec: { kind: 'empty' } };
  }

  const shapes = graph.nodes
    .filter((node) => entryNodeIds.has(node.id))
    .map((node) => node.inputShape)
    .filter(
      (shape): shape is string =>
        shape !== null && shape !== undefined && shape.trim() !== ''
    )
    .map(normalizeInputShape);

  if (shapes.length === 0) {
    return { ok: true, spec: { kind: 'empty' } };
  }

  const uniqueShapes = [...new Set(shapes)];
  if (uniqueShapes.length === 1) {
    const inputShape = uniqueShapes[0]!;
    const parsed = parseAgentInputShape(inputShape);
    if (parsed === null) {
      return { ok: true, spec: { kind: 'empty' } };
    }
    if (parsed.kind === 'struct') {
      return {
        ok: true,
        spec: { kind: 'struct', fields: parsed.fields, inputShape },
      };
    }
    return {
      ok: true,
      spec: { kind: 'scalar', type: parsed.type, inputShape },
    };
  }

  const parsedShapes = uniqueShapes
    .map((inputShape) => ({
      inputShape,
      parsed: parseAgentInputShape(inputShape),
    }))
    .filter(
      (
        entry
      ): entry is {
        inputShape: string;
        parsed: NonNullable<ReturnType<typeof parseAgentInputShape>>;
      } => entry.parsed !== null
    );

  if (parsedShapes.length !== uniqueShapes.length) {
    return { ok: false, reason: 'conflict' };
  }

  const allStruct = parsedShapes.every(
    (entry) => entry.parsed.kind === 'struct'
  );
  if (allStruct) {
    const merged = mergeStructFields(
      parsedShapes.map((entry) => ({
        fields: entry.parsed.kind === 'struct' ? entry.parsed.fields : [],
      }))
    );
    if (merged === null) {
      return { ok: false, reason: 'conflict' };
    }
    const inputShape = uniqueShapes.join(' | ');
    return {
      ok: true,
      spec: { kind: 'struct', fields: merged, inputShape },
    };
  }

  return { ok: false, reason: 'conflict' };
}

export function defaultSampleForEntryRunInputSpec(
  spec: WorkflowEntryRunInputSpec
): unknown {
  if (spec.kind === 'empty') {
    return {};
  }
  return PersistedWorkflow.sampleJsonFromOutputShape(spec.inputShape) ?? {};
}

export function buildRunInputFromFormValues(
  spec: WorkflowEntryRunInputSpec,
  values: Record<string, unknown>
): unknown {
  if (spec.kind === 'empty') {
    return {};
  }
  if (spec.kind === 'scalar') {
    const scalarValue = values['__scalar__'];
    if (scalarValue === undefined || scalarValue === '') {
      return {};
    }
    return scalarValue;
  }
  const output: Record<string, unknown> = {};
  for (const field of spec.fields) {
    const value = values[field.name];
    if (value === undefined || value === '') {
      if (field.required) {
        continue;
      }
      continue;
    }
    output[field.name] = value;
  }
  return output;
}

export function validateEntryRunInputFormValues(
  spec: WorkflowEntryRunInputSpec,
  values: Record<string, unknown>
): string | null {
  if (spec.kind === 'empty') {
    return null;
  }
  if (spec.kind === 'scalar') {
    return null;
  }
  for (const field of spec.fields) {
    if (!field.required) {
      continue;
    }
    const value = values[field.name];
    if (value === undefined || value === null || value === '') {
      return `${field.name} is required`;
    }
  }
  return null;
}
